import React, { createContext, useContext, useEffect, useState } from 'react';
import { BASE_URL } from '../config/config';

const CategoryContext = createContext();

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${BASE_URL}/categories`);
      const json = await res.json();

      if (res.ok && json.status === 200) {
        // Transform the data to include subcategory counts and flatten structure
        const transformedCategories = json.data.map(category => {
          // Calculate total count including subcategories
          const subcategoryCount = category.subcategories?.reduce((sum, sub) => sum + parseInt(sub.count || 0), 0) || 0;

          return {
            ...category,
            count: subcategoryCount || 0, // Use subcategory count or 0 if no subcategories
            subcategories: category.subcategories || []
          };
        });

        setCategories(transformedCategories);
      } else {
        throw new Error(json.message || 'Failed to fetch categories');
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Helper function to get all category names (including subcategories)
  const getAllCategoryNames = () => {
    const names = [];
    categories.forEach(category => {
      names.push(category.name);
      if (category.subcategories) {
        category.subcategories.forEach(sub => {
          names.push(sub.sub_cat_name);
        });
      }
    });
    return names;
  };

  // Helper function to get category by name
  const getCategoryByName = (name) => {
    for (const category of categories) {
      if (category.name === name) {
        return category;
      }
      if (category.subcategories) {
        const subcategory = category.subcategories.find(sub => sub.sub_cat_name === name);
        if (subcategory) {
          return { ...subcategory, parentCategory: category.name };
        }
      }
    }
    return null;
  };

  const value = {
    categories,
    loading,
    error,
    getAllCategoryNames,
    getCategoryByName,
    refetch: fetchCategories
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};
